import { Control, Controller, FieldErrors } from 'react-hook-form';

import FieldRow from 'components/FormElements/FieldRow';

import { Grid } from '@mui/material';
import AppSelectBox from 'components/FormElements/AppSelectBox';

import { NumericFormat } from 'react-number-format';
import { AppTextInput, FormLabel } from 'components/FormElements';
import { Divider, IconButton, styled } from '@mui/material';
import Button from 'components/Button';
import { Fragment } from 'react';
import { Delete } from '@mui/icons-material';
import Spacing from 'components/Spacing';
import ButtonGroup from 'components/ButtonGroup';
import GridContainer from 'components/GridContainer';
import Row from 'components/Layout/Row';
import { BankAccountSettingsFormValues } from './BankAccountSettings';
import { Error } from 'components/FormElements/LayoutComponents';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const FeeButton = styled(Button)(() => ({
  padding: '0 !important',
}));

type Fee = {
  id?: string;
  amount: number;
  vat: number;
  rangeStart?: number | null;
  rangeEnd?: number | null;
};

type WithdrawalFeesProps = {
  fees: Array<Fee>;
  append: (fee: Fee) => void;
  remove: (index: number) => void;
  control: Control<BankAccountSettingsFormValues>;
  errors: FieldErrors<BankAccountSettingsFormValues>;
};
const WithdrawalFees = ({
  fees = [],
  append,
  remove,
  control,
  errors,
}: WithdrawalFeesProps) => {
  const [canEditFirm] = useHasPermissions([RolePermission.EditFirm]);

  return (
    <Spacing>
      <p>
        Use this section to manage any withdrawal fees. Multiple withdrawal fees
        can be set, if you have different fees for small or large withdrawals.
      </p>

      {!fees.length && canEditFirm ? (
        <ButtonGroup>
          <FeeButton
            variant="text"
            color="primary"
            onClick={() => {
              append({
                amount: 0,
                vat: 0,
                rangeStart: 0,
                rangeEnd: null,
              });
            }}
          >
            <b>Add Fee</b>
          </FeeButton>
        </ButtonGroup>
      ) : null}

      {fees.map((fee, index) => (
        <Fragment key={fee.id}>
          <FieldRow title="Enter withdrawal fee" centerTitle>
            <GridContainer>
              <Grid size={6}>
                <Controller
                  name={`withdrawalFees.${index}.amount`}
                  defaultValue={fee.amount}
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <Row centered spacing="sm">
                      <NumericFormat
                        {...rest}
                        inputRef={ref}
                        prefix="£"
                        onValueChange={({ value }) => {
                          onChange(+value);
                        }}
                        allowNegative={false}
                        decimalScale={2}
                        allowLeadingZeros={false}
                        thousandSeparator={true}
                        customInput={AppTextInput}
                        error={!!error}
                        helperText={error?.message}
                      />
                    </Row>
                  )}
                />
              </Grid>

              <Grid size={6}>
                <Controller
                  name={`withdrawalFees.${index}.vat`}
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <Row centered spacing="sm">
                      <FormLabel title="VAT" />
                      <AppSelectBox
                        required
                        {...rest}
                        inputRef={ref}
                        data={[
                          { label: '20% VAT', value: 20 },
                          { label: 'No VAT', value: 0 },
                        ]}
                        name="vat"
                        valueKey="value"
                        labelKey="label"
                        variant="outlined"
                        onChange={onChange}
                        error={!!error}
                        helperText={error?.message}
                      />
                    </Row>
                  )}
                />
              </Grid>
            </GridContainer>
          </FieldRow>

          <FieldRow title=" ">
            <FormLabel title="Range (if applicable - leave blank if fee applies to all withdrawals)" />
          </FieldRow>

          <FieldRow title=" ">
            <GridContainer>
              <Grid size={6}>
                <Controller
                  name={`withdrawalFees.${index}.rangeStart`}
                  defaultValue={fee.rangeStart}
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <Row centered spacing="sm">
                      <FormLabel title="From" />
                      <NumericFormat
                        {...rest}
                        inputRef={ref}
                        onValueChange={({ value }) => {
                          onChange(value ? +value : null);
                        }}
                        decimalScale={2}
                        prefix="£"
                        allowNegative={false}
                        allowLeadingZeros={false}
                        thousandSeparator={true}
                        customInput={AppTextInput}
                        error={!!error}
                        helperText={error?.message}
                      />
                    </Row>
                  )}
                />
              </Grid>

              <Grid size={6}>
                <Controller
                  name={`withdrawalFees.${index}.rangeEnd`}
                  defaultValue={fee.rangeEnd}
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <Row centered spacing="sm">
                      <FormLabel title="To" />
                      <NumericFormat
                        {...rest}
                        inputRef={ref}
                        onValueChange={({ value }) => {
                          onChange(value ? +value : null);
                        }}
                        decimalScale={2}
                        prefix="£"
                        allowNegative={false}
                        allowLeadingZeros={false}
                        thousandSeparator={true}
                        customInput={AppTextInput}
                        error={!!error}
                        helperText={error?.message}
                      />
                    </Row>
                  )}
                />
              </Grid>
            </GridContainer>
          </FieldRow>

          {canEditFirm ? (
            <ButtonGroup>
              <IconButton onClick={() => remove(index)} size="large">
                <Delete />
              </IconButton>

              {(index === fees.length - 1 || !fees.length) && (
                <FeeButton
                  variant="text"
                  color="primary"
                  onClick={() => {
                    append({
                      // Set the new range to the previous fee's amount + 0.01
                      amount: fee.amount || 0,
                      vat: fee.vat || 0,
                      rangeStart: fee.rangeEnd ? fee.rangeEnd + 0.01 : null,
                      rangeEnd: null,
                    });
                  }}
                >
                  <b>Add another fee and range</b>
                </FeeButton>
              )}
            </ButtonGroup>
          ) : null}

          {index !== fees.length - 1 && (
            <Divider variant="middle" sx={{ opacity: 0.6 }} />
          )}
        </Fragment>
      ))}

      {!!errors?.withdrawalFees?.root?.message && (
        <Error>{errors?.withdrawalFees?.root?.message}</Error>
      )}

      <FieldRow
        title="Withdrawal text"
        informationText="Use this field to explain the withdrawal fee to investors"
        centerTitle
      >
        <Controller
          name="withdrawalFeeText"
          control={control}
          disabled={!canEditFirm}
          render={({ field: { ref, ...rest }, fieldState: { error } }) => (
            <AppTextInput
              {...rest}
              inputRef={ref}
              placeholder="e.g. There is a withdrawal fee of £10 for any withdrawals under £10,000."
              fullWidth
              multiline
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FieldRow>
    </Spacing>
  );
};

export default WithdrawalFees;
